import * as React from 'react';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { Alert, Button, Empty, Popconfirm, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
    AddAdditionalClassificationModal,
    AdditionalClassificationsActionsRenderer,
    RefreshAdditionalClassificationModal
} from '..';
import { ApplicationDefinitionConditionalEditStore } from '../../stores';
import { AdditionalClassificationDto } from '../../types';
import { flattenClassifications, classificationOccurrenceKeys, classificationPath } from '../../misc/classifications';
import { AdditionalClassificationDraggableRow } from './AdditionalClassificationDraggableRow';
import './AdditionalClassificationsRenderer.less';
import {
    classificationMismatchMessage,
    classificationVerificationIncompleteMessage
} from '../../types/AdditionalClassificationVerification';

interface Props {
    store: ApplicationDefinitionConditionalEditStore;
    canEdit: boolean;
    tabActions?: React.ReactNode;
}

interface AdditionalClassificationTableRow extends AdditionalClassificationDto {
    tableKey: string;
    isRoot: boolean;
    rootIndex?: number;
    children: AdditionalClassificationTableRow[];
    refreshClassification: AdditionalClassificationDto;
}

const tableComponents = {
    body: {
        row: AdditionalClassificationDraggableRow
    }
};

const AdditionalClassificationsRenderer: React.FC<Props> = ({ store, canEdit, tabActions }) => {
    const canMutateClassifications = store.canMutateClassifications;

    const getStructure = (classification: AdditionalClassificationTableRow): string[] => {
        if (!classification.isRoot) {
            return [];
        }

        const badges: string[] = [];

        if (classification.children.length > 0) {
            badges.push('Has dependent classification');
        }

        if (classification.standardParent) {
            badges.push('Depends on standard classification');
        }

        return badges.length ? badges : ['Standalone'];
    };

    const renderDeleteConfirmation = (classification: AdditionalClassificationTableRow) => {
        const fields = flattenClassifications([classification]);

        return (
            <div className="additional-classifications-renderer__delete-confirmation">
                <div>Delete this classification and the included fields?</div>
                <ul>
                    {fields.map(field => (
                        <li key={`${field.typeCode}-${field.classificationSlot}`}>
                            <span className="additional-classifications-renderer__delete-confirmation-name">
                                {field.displayName} ({field.classificationSlot})
                            </span>
                            <span className="additional-classifications-renderer__delete-confirmation-slot">
                                {field.systemName}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const toTableRows = (
        classifications: AdditionalClassificationDto[],
        parentKey?: string,
        rootClassification?: AdditionalClassificationDto
    ): AdditionalClassificationTableRow[] => {
        const occurrenceKeys = classificationOccurrenceKeys(classifications);
        return classifications.map((classification, index) => {
            const tableKey = classificationPath(parentKey, occurrenceKeys[index]);
            const isRoot = parentKey === undefined;

            return {
                ...classification,
                tableKey,
                isRoot,
                rootIndex: isRoot ? index : undefined,
                children: toTableRows(classification.children ?? [], tableKey, rootClassification ?? classification),
                refreshClassification: rootClassification ?? classification
            };
        });
    };

    const getExpandedRowKeys = (rows: AdditionalClassificationTableRow[]): string[] =>
        rows.reduce<string[]>((keys, row) => [...keys, row.tableKey, ...getExpandedRowKeys(row.children)], []);

    const tableRows = toTableRows(store.additionalClassifications);
    const moveRow = (dragIndex: number, dropIndex: number) => {
        store.moveAdditionalClassificationToIndex(dragIndex, dropIndex);
    };

    const columns: ColumnsType<AdditionalClassificationTableRow> = [
        {
            title: 'Order',
            key: 'order',
            width: 96,
            render: (_value, record) =>
                record.isRoot ? (
                    <div className="additional-classifications-renderer__order">
                        {canMutateClassifications ? (
                            <span
                                aria-label={`Drag ${record.displayName}`}
                                className="additional-classifications-renderer__drag-handle"
                            />
                        ) : null}
                        <span aria-label={`Order ${record.rootIndex! + 1}`}>{record.rootIndex! + 1}</span>
                    </div>
                ) : null
        },
        {
            title: 'Classification',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (_value, record) => (
                <div className="additional-classifications-renderer__classification">
                    <div className="group-title">
                        {record.displayName}
                        {store.classificationVerification.mismatchPaths.includes(record.tableKey) ? (
                            <Tooltip
                                trigger={['hover', 'focus', 'click']}
                                title={
                                    <div>
                                        <div>{classificationMismatchMessage}</div>
                                        {canEdit ? (
                                            <Button
                                                aria-label="Refresh classification"
                                                type="link"
                                                size="small"
                                                className="additional-classifications-renderer__mismatch-refresh"
                                                icon={<ReloadOutlined />}
                                                disabled={
                                                    store.isLoadingAdditionalClassificationRefresh ||
                                                    !canMutateClassifications
                                                }
                                                onClick={() =>
                                                    store.refreshAdditionalClassificationFromSics(
                                                        record.refreshClassification
                                                    )
                                                }
                                            >
                                                Refresh classification
                                            </Button>
                                        ) : null}
                                    </div>
                                }
                            >
                                <Button
                                    type="link"
                                    danger
                                    size="small"
                                    aria-label={
                                        `SICS mismatch for ${record.displayName}. ${classificationMismatchMessage} ` +
                                        `Refresh ${record.refreshClassification.displayName} from SICS using the row action.`
                                    }
                                    icon={<ExclamationCircleOutlined />}
                                />
                            </Tooltip>
                        ) : null}
                    </div>
                    <div className="group-subtitle">{record.systemName}</div>
                </div>
            )
        },
        {
            title: 'SICS Slot',
            dataIndex: 'classificationSlot',
            key: 'classificationSlot',
            width: 170
        },
        {
            title: 'Structure',
            key: 'structure',
            width: 210,
            render: (_value, record) => (
                <>
                    {getStructure(record).map(structure => (
                        <div key={structure}>{structure}</div>
                    ))}
                </>
            )
        },
        {
            title: 'Included fields',
            key: 'includedFields',
            width: 140,
            render: (_value, record) => flattenClassifications([record]).length
        }
    ];

    if (canEdit) {
        columns.push({
            title: 'Actions',
            key: 'actions',
            width: 112,
            render: (_value, record) =>
                record.isRoot ? (
                    <div className="row-actions-wrapper additional-classifications-renderer__actions">
                        <Tooltip placement="top" title="Refresh from SICS">
                            <Button
                                aria-label={`Refresh ${record.displayName} from SICS`}
                                data-id={`additional-classification-refresh-${record.classificationSlot}`}
                                type="link"
                                size="small"
                                loading={
                                    store.isLoadingAdditionalClassificationRefresh &&
                                    store.refreshingAdditionalClassificationTypeCode === record.typeCode
                                }
                                disabled={store.isLoadingAdditionalClassificationRefresh || !canMutateClassifications}
                                icon={<ReloadOutlined className="additional-classifications-renderer__refresh-icon" />}
                                onClick={() =>
                                    store.refreshAdditionalClassificationFromSics(record.refreshClassification)
                                }
                            />
                        </Tooltip>
                        <Popconfirm
                            title={renderDeleteConfirmation(record)}
                            disabled={!canMutateClassifications}
                            onConfirm={() => store.removeAdditionalClassification(record)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Tooltip placement="top" title="Delete classification">
                                <Button
                                    aria-label={`Remove ${record.displayName}`}
                                    data-id={`additional-classification-delete-${record.classificationSlot}`}
                                    disabled={!canMutateClassifications}
                                    type="link"
                                    size="small"
                                >
                                    <i className="alpha-icon md delete-icon-red" />
                                </Button>
                            </Tooltip>
                        </Popconfirm>
                    </div>
                ) : null
        });
    }

    return (
        <div className="additional-classifications-renderer" data-id="additional-classifications-renderer">
            {canEdit ? <AddAdditionalClassificationModal store={store} /> : null}
            {canEdit ? <RefreshAdditionalClassificationModal store={store} /> : null}

            <AdditionalClassificationsActionsRenderer store={store} canEdit={canEdit} tabActions={tabActions} />

            {store.classificationVerification.isVerifying ? (
                <Alert
                    type="info"
                    showIcon
                    message="Checking classifications against SICS…"
                    style={{ marginBottom: 16 }}
                />
            ) : null}

            {store.classificationVerification.verificationIncomplete ? (
                <Alert
                    type="warning"
                    showIcon
                    message={classificationVerificationIncompleteMessage}
                    action={
                        <Button
                            size="small"
                            loading={store.classificationVerification.isVerifying}
                            disabled={store.classificationVerification.isVerifying}
                            onClick={() => store.classificationVerification.verify(true)}
                        >
                            Retry verification
                        </Button>
                    }
                    style={{ marginBottom: 16 }}
                />
            ) : null}

            <DndProvider backend={HTML5Backend}>
                <Table<AdditionalClassificationTableRow>
                    rowKey="tableKey"
                    className="alpha-portal-table additional-classifications-renderer__table"
                    columns={columns}
                    dataSource={tableRows}
                    pagination={false}
                    rowClassName={record => (record.isRoot ? 'additional-classifications-renderer__root-row' : '')}
                    components={
                        canMutateClassifications && store.additionalClassifications.length > 1
                            ? tableComponents
                            : undefined
                    }
                    onRow={
                        canMutateClassifications && store.additionalClassifications.length > 1
                            ? record =>
                                  ({
                                      index: record.rootIndex,
                                      record,
                                      moveRow
                                  }) as React.HTMLAttributes<HTMLElement>
                            : undefined
                    }
                    expandable={{
                        expandedRowKeys: getExpandedRowKeys(tableRows),
                        expandIcon: () => null,
                        indentSize: 20
                    }}
                    locale={{
                        emptyText: <Empty description="No additional classifications configured" />
                    }}
                />
            </DndProvider>
        </div>
    );
};

export default observer(AdditionalClassificationsRenderer);
